import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { isAuthed, clearToken } from "@/lib/auth";
import { Search } from "lucide-react";

const navItems = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/shop", label: "Shop", testid: "nav-shop" },
  { to: "/raffles", label: "Raffles", testid: "nav-raffles" },
  { to: "/#reviews", label: "Reviews", testid: "nav-reviews", hash: "reviews" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Header() {
  const nav = useNavigate();
  const loc = useLocation();
  const authed = isAuthed();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");

  const scrollToHash = (hash) => {
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleHashClick = (e, hash) => {
    if (loc.pathname === "/") {
      e.preventDefault();
      scrollToHash(hash);
      window.history.replaceState(null, "", `/#${hash}`);
    }
    // otherwise let React Router navigate to "/" then we scroll via effect on Home
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const trimmed = q.trim();
    if (!trimmed) {
      nav("/shop");
    } else {
      nav(`/shop?q=${encodeURIComponent(trimmed)}`);
    }
    setSearchOpen(false);
    setQ("");
  };

  return (
    <header
      data-testid="site-header"
      className="bg-[#0F0C0B] border-b border-[#332D2B] sticky top-0 z-40"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-3 group"
        >
          <div className="w-10 h-10 border border-[#C8A96A] flex items-center justify-center">
            <span className="font-display text-[#C8A96A] text-xl leading-none">FZ</span>
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-2xl text-[#F2EBDD] tracking-wide">football.zone</span>
            <span className="font-mono-retro text-[10px] tracking-[0.3em] text-[#A8A39D] uppercase">est. 2026 — vintage club</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              end={n.to === "/"}
              onClick={n.hash ? (e) => handleHashClick(e, n.hash) : undefined}
              className={({ isActive }) =>
                `font-mono-retro text-xs uppercase tracking-[0.25em] transition-colors ${
                  isActive && !n.hash ? "text-[#C8A96A]" : "text-[#A8A39D] hover:text-[#F2EBDD]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-search-toggle"
            onClick={() => setSearchOpen((v) => !v)}
            aria-label="Search jerseys"
            className="text-[#A8A39D] hover:text-[#C8A96A] p-2 border border-transparent hover:border-[#C8A96A] transition-colors"
          >
            <Search size={16} />
          </button>
          {authed ? (
            <>
              <Link to="/admin" data-testid="header-admin-link" className="btn-ghost text-xs">Admin</Link>
              <button data-testid="header-logout" className="btn-ghost text-xs" onClick={() => { clearToken(); nav("/"); }}>Logout</button>
            </>
          ) : (
            <Link to="/admin/login" data-testid="header-admin-login" className="btn-ghost text-xs">Host Login</Link>
          )}
        </div>
      </div>

      {/* Expandable search row */}
      {searchOpen && (
        <div data-testid="header-search-panel" className="border-t border-[#332D2B] bg-[#1C1816]">
          <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-3">
            <Search size={18} className="text-[#C8A96A]" />
            <input
              data-testid="header-search-input"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search team, country, player, season…"
              className="flex-1 bg-transparent border-0 outline-none py-2 text-[#F2EBDD] placeholder:text-[#756F6B] font-body"
            />
            <button data-testid="header-search-submit" type="submit" className="btn-secondary text-xs">Go</button>
            <button type="button" onClick={() => { setSearchOpen(false); setQ(""); }} className="font-mono-retro text-[10px] tracking-[0.3em] uppercase text-[#A8A39D] hover:text-[#C8A96A]">Esc</button>
          </form>
        </div>
      )}
    </header>
  );
}
